import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { 
    AlertCircle, ArrowLeft, Loader2, Save, Eye, Palette, 
    Laptop2, CheckCircle2, HelpCircle, Plus, X,
    MessageSquare, Phone, Settings, Sparkles, Webhook, Check, Copy, Code, Trash2,
    AlertTriangle, CheckCircle, Type, Link, Globe, Hash, Bot, FileText,
    MapPin, Paintbrush, Wand2, LayoutGrid, ToggleLeft
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Combobox } from '@/components/ui/combobox';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { SwitchWithLabel } from '@/components/ui/switch-with-label';
import { Textarea } from '@/components/ui/textarea';
import { createWidget, getWidgetById, updateWidget, deleteWidget } from '@/api/widgets/widgets.api';
import { getAssistants } from '@/api/assistants/assistants.api';
import type { WidgetResponse, CreateWidgetRequest, UpdateWidgetRequest } from '@/api/widgets/widgets.types';
import type { Assistant } from '@/api/assistants/assistants.types';
import { useAuth } from '@/context/AuthContext';
import { WidgetPreview } from './components/widget-preview';
import { MobileOverlay } from '@/components/ui/mobile-overlay';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { IconPicker } from "@/components/ui/icon-picker";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WidgetIntegrationGuide } from "./components/widget-integration-guide";
import { SiteHeader } from '@/components/site-header';

const MAX_NAME_LENGTH = 50;

// Font family options for the dropdown
const fontOptions = [
  { value: 'inter', label: 'Inter (Default)', popular: true },
  { value: 'roboto', label: 'Roboto', popular: true },
  { value: 'open-sans', label: 'Open Sans', popular: true },
  { value: 'poppins', label: 'Poppins' },
  { value: 'lato', label: 'Lato' },
  { value: 'montserrat', label: 'Montserrat' },
  { value: 'nunito', label: 'Nunito' },
  { value: 'source-sans', label: 'Source Sans 3' },
  { value: 'system', label: 'System Font (Fast)' }
];

const defaultWidgetConfig: CreateWidgetRequest = {
    name: '',
    webhookUrl: '',
    allowedOrigins: ['*'],
    chatEnabled: true, // Default: enable chat
    uiConfig: {
        brandColor: '#036ffc',
        title: 'Chat with us',
        subtitle: 'We\'re here to help',
        toggleText: 'Talk to us',
        fontFamily: 'inter',
        placeholder: 'Type your message...',
        position: 'bottom-right',
        showWhatsApp: true,
        showCall: true,
        whatsAppUrl: 'https://wa.me/15551234567',
        whatsAppLabel: 'Contact us on WhatsApp',
        callUrl: 'tel:+15551234567',
        callLabel: 'Contact us by phone',
        phoneNumber: '+15551234567',
        mainCardTitle: 'Send us message',
        mainCardDescription: 'Usual reply time is a few seconds',
        mainCardIcon: 'message-square',
        mainCardColor: '#036ffc',
        chatHeaderTitle: 'Sales Assistant',
        chatHeaderSubtitle: 'Always here to help you',
        welcomeMessage: '👋 Hello! How can I be of assistance today?',
        enableRainbowRing: true,
        rainbowRingColors: ['#ff0080', '#ff8000', '#ffff00', '#80ff00', '#00ff80', '#00ffff', '#0080ff', '#8000ff'],
        toggleIcon: 'messages-square',
        privacyPolicyUrl: 'https://yourdomain.com/privacy',
        customCards: [
            {
                id: 'help-center',
                title: 'Help Center',
                description: 'Browse our knowledge base',
                icon: 'help-circle',
                url: 'https://help.example.com',
                color: '#3b82f6',
                enabled: true
            },
            {
                id: 'documentation',
                title: 'Documentation',
                description: 'Technical guides and tutorials',
                icon: 'book-open',
                url: 'https://docs.example.com',
                color: '#10b981',
                enabled: true
            }
        ],
    },
    voiceConfig: {
        enabled: true,
        assistantId: '',
        title: 'Start Voice Call',
        description: 'Talk directly with our AI assistant',
        icon: 'phone',
        color: '#10b981'
    },
};

export default function WidgetConfigNew() {
    const navigate = useNavigate();
    const { id: widgetId } = useParams();
    const { success, error } = useEnhancedToast();
    const { user } = useAuth();

    const [config, setConfig] = useState<CreateWidgetRequest>(defaultWidgetConfig);
    const [widgetData, setWidgetData] = useState<WidgetResponse | null>(null);
    const [assistants, setAssistants] = useState<Assistant[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(!!widgetId);
    const [nameError, setNameError] = useState('');
    const [webhookError, setWebhookError] = useState('');
    const [webhookUrl, setWebhookUrl] = useState<string>('');
    const [newOrigin, setNewOrigin] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const canEdit = user?.permissions?.widgets === 'EDITOR';
    const isEditing = !!widgetId;

    // Load data on mount
    useEffect(() => {
        const loadData = async () => {
            try {
                // Load assistants
                const assistantsResponse = await getAssistants();
                if (assistantsResponse.data) {
                    setAssistants(assistantsResponse.data);
                }

                // Load widget data if editing
                if (widgetId) {
                    const response = await getWidgetById(widgetId);
                    if (response.error) {
                        error({
                            icon: AlertCircle,
                            title: "Error Loading Widget",
                            description: response.error,
                        });
                        navigate('/dashboard/widgets');
                    } else if (response.data) {
                        const widget = response.data;
                        setWidgetData(widget);
                        setConfig({
                            name: widget.name,
                            chatbotId: widget.chatbotId,
                            webhookUrl: widget.webhookUrl,
                            allowedOrigins: widget.allowedOrigins || ['*'],
                            isActive: widget.isActive,
                            uiConfig: { ...defaultWidgetConfig.uiConfig, ...widget.uiConfig },
                            voiceConfig: { ...defaultWidgetConfig.voiceConfig, ...widget.voiceConfig },
                            hmacSecret: widget.hmacSecret,
                        });
                        setWebhookUrl(widget.webhookUrl);
                    }
                }
                } catch {
                error({
                    icon: AlertCircle,
                    title: "Error Loading Data",
                    description: "Failed to load widget data",
                });
            } finally {
                setIsLoadingData(false);
            }
        };

        loadData();
    }, [widgetId]);

    // Configuration update handlers
    const handleConfigUpdate = (updates: Partial<CreateWidgetRequest>) => {
        setConfig(prev => ({ ...prev, ...updates }));
        setHasUnsavedChanges(true);
    };

    const validateWebhookUrl = (url: string): boolean => {
        if (!url) return true;
        const validPattern = /^https:\/\/(dev\.)?(workflow\.(amira\.bot|voicebot\.studio))\/api\/v1\/webhooks\/[a-zA-Z0-9]+\/sync$/;
        return validPattern.test(url);
    };

    const handleWebhookUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const url = e.target.value;
        setWebhookUrl(url);
    };

    const handleWebhookUrlBlur = () => {
        const isValidUrl = validateWebhookUrl(webhookUrl);
        const errorMsg = webhookUrl && !isValidUrl 
            ? "URL must match format: https://workflow.voicebot.studio/api/v1/webhooks/[ID]/sync" 
            : '';
        setWebhookError(errorMsg);
        
        if (!webhookUrl || isValidUrl) {
            handleConfigUpdate({ webhookUrl: webhookUrl });
        }
    };

    const handleUIConfigUpdate = (updates: Partial<CreateWidgetRequest['uiConfig']>) => {
        setConfig(prev => ({
            ...prev,
            uiConfig: { ...prev.uiConfig, ...updates }
        }));
        setHasUnsavedChanges(true);
    };

    const handleVoiceConfigUpdate = (updates: Partial<CreateWidgetRequest['voiceConfig']>) => {
        setConfig(prev => ({
            ...prev,
            voiceConfig: { ...prev.voiceConfig, ...updates }
        }));
        setHasUnsavedChanges(true);
    };

    const addOrigin = () => {
        if (newOrigin.trim() && !config.allowedOrigins?.includes(newOrigin.trim())) {
            const origins = [...(config.allowedOrigins || []), newOrigin.trim()];
            handleConfigUpdate({ allowedOrigins: origins });
            setNewOrigin('');
        }
    };

    const removeOrigin = (origin: string) => {
        const origins = config.allowedOrigins?.filter(o => o !== origin) || [];
        handleConfigUpdate({ allowedOrigins: origins });
    };


    const validateForm = (): boolean => {
        let isValid = true;

        if (!config.name.trim()) {
            setNameError('Widget name is required');
            error({
                icon: AlertCircle,
                title: "Validation Error",
                description: "Widget name is required.",
            });
            isValid = false;
        } else if (config.name.length > MAX_NAME_LENGTH) {
            setNameError(`Name must be ${MAX_NAME_LENGTH} characters or less`);
            error({
                icon: AlertCircle,
                title: "Validation Error",
                description: `Widget name must be ${MAX_NAME_LENGTH} characters or less.`,
            });
            isValid = false;
        } else {
            setNameError('');
        }

        // Webhook URL is required only when chat is enabled
        if (config.chatEnabled) {
            if (!webhookUrl.trim()) {
                setWebhookError('Webhook URL is required when chat is enabled');
                error({
                    icon: AlertCircle,
                    title: "Validation Error",
                    description: "Webhook URL is required when chat is enabled.",
                });
                isValid = false;
            } else if (!validateWebhookUrl(webhookUrl)) {
                setWebhookError('URL must match format: https://workflow.voicebot.studio/api/v1/webhooks/[ID]/sync');
                error({
                    icon: AlertCircle,
                    title: "Validation Error",
                    description: "Webhook URL must end with /sync and match the required format.",
                });
                isValid = false;
            } else {
                setWebhookError('');
            }
        } else {
            setWebhookError('');
        }

        // Assistant is required only when voice is enabled
        if (config.voiceConfig?.enabled && !config.voiceConfig?.assistantId) {
            error({
                icon: AlertCircle,
                title: "Validation Error",
                description: "Please select a voice assistant when voice calls are enabled.",
            });
            isValid = false;
        }

        return isValid;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const payload = { ...config, webhookUrl };
            
            if (isEditing && widgetId) {
                const response = await updateWidget(widgetId, payload as UpdateWidgetRequest);
                if (response.error) {
                    error({
                        icon: AlertCircle,
                        title: "Update Failed",
                        description: response.error,
                    });
                } else {
                    success({
                        icon: CheckCircle2,
                        title: "Widget Updated",
                        description: "Your widget has been updated successfully",
                    });
                    // Stay on the current edit page - no navigation
                    setWidgetData(response.data || null);
                    setHasUnsavedChanges(false);
                }
            } else {
                const response = await createWidget(payload);
                if (response.error) {
                    error({
                        icon: AlertCircle,
                        title: "Creation Failed",
                        description: response.error,
                    });
                } else {
                    success({
                        icon: CheckCircle2,
                        title: "Widget Created",
                        description: "Your widget has been created successfully",
                    });
                    // Navigate to edit mode for the newly created widget
                    navigate(`/dashboard/widgets/${response.data?.id}`);
                    setHasUnsavedChanges(false);
                }
            }
        } catch {
            error({
                icon: AlertCircle,
                title: "Error",
                description: "An unexpected error occurred",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!isEditing || !widgetId) return;

        setIsDeleting(true);
        try {
            const response = await deleteWidget(widgetId);
            if (response.error) {
                error({
                    icon: AlertCircle,
                    title: "Delete Failed",
                    description: response.error,
                    duration: 4000
                });
            } else {
                success({
                    icon: CheckCircle2,
                    title: "Widget Deleted",
                    description: "The widget has been permanently removed",
                    duration: 3000
                });
                setDeleteDialogOpen(false);
                navigate('/dashboard/widgets');
            }
        } catch (err: unknown) {
            error({
                icon: AlertCircle,
                title: "Deletion Failed",
                description: err instanceof Error ? err.message : "Failed to delete widget. Please try again.",
                duration: 4000
            });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="h-full flex flex-col">
            <MobileOverlay 
                icon={Laptop2}
                title="Desktop View Only"
                description="Widget configuration is optimized for desktop view."
                onBack={() => navigate('/dashboard/widgets')}
            />
            
            {/* Main Content - Hidden on mobile */}
            <div className="hidden sm:flex sm:flex-col sm:h-full">
                <SiteHeader
                    title={isEditing ? 'Widget Configuration' : 'Create Widget'}
                    subtitle="Configure your customizable widget for AI assistants"
                    backButton={
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/dashboard/widgets')}
                            className="gap-2"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </Button>
                    }
                >
                    <>
                        {isEditing && (
                            canEdit ? (
                                <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                                    <DialogTrigger asChild>
                                        <Button variant="outline" size="sm" className="text-destructive hover:text-destructive gap-2">
                                            <Trash2 className="h-4 w-4" />
                                            Delete
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle className="text-base">Delete Widget</DialogTitle>
                                            <DialogDescription>
                                                This action cannot be undone. The widget will be permanently deleted.
                                            </DialogDescription>
                                        </DialogHeader>
                                        <DialogFooter>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setDeleteDialogOpen(false)}
                                                disabled={isDeleting}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={handleDelete}
                                                disabled={isDeleting}
                                            >
                                                {isDeleting ? (
                                                    <>
                                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                        Deleting
                                                    </>
                                                ) : (
                                                    'Delete Widget'
                                                )}
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            ) : (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-muted-foreground gap-2"
                                    disabled
                                    title="You need editor access to delete widgets"
                                >
                                    <Trash2 className="h-4 w-4" />
                                    Delete
                                </Button>
                            )
                        )}
                        <Button
                            onClick={handleSave}
                            disabled={isLoading || !canEdit}
                            size="sm"
                            className="gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4" />
                                    {isEditing ? 'Save Changes' : 'Create Widget'}
                                </>
                            )}
                        </Button>
                    </>
                </SiteHeader>

                <div className="flex-1 overflow-auto px-4 py-6 md:px-8 md:py-8">
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                    >
                        {/* Configuration Form */}
                        <div key="configuration-form" className="space-y-6">
                            <Tabs defaultValue="general" className="space-y-4" layoutId="widget-main-tabs">
                                <div className="overflow-x-auto">
                                    <TabsList className="h-11">
                                        <TabsTrigger value="general" className="gap-2 px-4">
                                            <Settings className="h-4 w-4" />
                                            General
                                        </TabsTrigger>
                                        <TabsTrigger value="chat" className="gap-2 px-4">
                                            <MessageSquare className="h-4 w-4" />
                                            Chat
                                        </TabsTrigger>
                                        <TabsTrigger value="voice" className="gap-2 px-4">
                                            <Phone className="h-4 w-4" />
                                            Voice
                                        </TabsTrigger>
                                        <TabsTrigger value="ui" className="gap-2 px-4">
                                            <Palette className="h-4 w-4" />
                                            Appearance
                                        </TabsTrigger>
                                        <TabsTrigger value="cards" className="gap-2 px-4">
                                            <Sparkles className="h-4 w-4" />
                                            Cards
                                        </TabsTrigger>
                                        <TabsTrigger value="integration" className="gap-2 px-4">
                                            <Code className="h-4 w-4" />
                                            Integration
                                        </TabsTrigger>
                                    </TabsList>
                                </div>

                                 {/* General Tab */}
                                 <TabsContent value="general" className="m-0">
                                     <motion.div
                                         initial={{ opacity: 0, y: 10 }}
                                         animate={{ opacity: 1, y: 0 }}
                                         exit={{ opacity: 0, y: -10 }}
                                         transition={{ duration: 0.2 }}
                                     >
                                         <Card className="shadow-sm">
                                         <CardHeader className="border-b bg-muted/20">
                                             <div className="flex items-center gap-3">
                                                 <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                                     <Settings className="h-4 w-4 text-primary" />
                                                 </div>
                                                 <div className="flex-1 space-y-1">
                                                     <CardTitle>General Configuration</CardTitle>
                                                     <CardDescription>Basic widget settings and privacy options</CardDescription>
                                                 </div>
                                             </div>
                                         </CardHeader>
                                         <CardContent className="space-y-6">
                                             {isLoadingData ? (
                                                 <div className="space-y-4">
                                                     <Skeleton className="h-10 w-full" />
                                                     <Skeleton className="h-10 w-full" />
                                                 </div>
                                             ) : (
                                                 <>
                                                    {/* Show Chatbot ID for existing widgets */}
                                                    {isEditing && widgetData?.chatbotId && (
                                                        <div className="space-y-2">
                                                            <Label htmlFor="chatbotId" className="flex items-center gap-2">
                                                                <Hash className="h-4 w-4 text-primary" />
                                                                Chatbot ID
                                                            </Label>
                                                            <div className="flex items-center gap-2">
                                                                <Input
                                                                    id="chatbotId"
                                                                    value={widgetData.chatbotId}
                                                                    readOnly
                                                                    className="h-9 font-mono text-sm flex-1 bg-muted/50"
                                                                />
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(widgetData.chatbotId);
                                                                        setIsCopied(true);
                                                                        setTimeout(() => setIsCopied(false), 2000);
                                                                    }}
                                                                    className="shrink-0"
                                                                >
                                                                    {isCopied ? (
                                                                        <Check className="h-4 w-4 text-green-500" />
                                                                    ) : (
                                                                        <Copy className="h-4 w-4" />
                                                                    )}
                                                                </Button>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground">
                                                                Unique identifier for your widget
                                                            </p>
                                                        </div>
                                                    )}

                                                    <div className="space-y-2">
                                                        <div className="flex items-center justify-between">
                                                            <Label htmlFor="name" className="flex items-center gap-2">
                                                                <Type className="h-4 w-4 text-primary" />
                                                                Widget Name
                                                                <span className="text-destructive">*</span>
                                                            </Label>
                                                            <span className="text-xs text-muted-foreground">
                                                                {config.name.length}/{MAX_NAME_LENGTH}
                                                            </span>
                                                        </div>
                                                        <Input
                                                            id="name"
                                                            value={config.name}
                                                            onChange={(e) => handleConfigUpdate({ name: e.target.value })}
                                                            placeholder="Enter widget name"
                                                            maxLength={MAX_NAME_LENGTH}
                                                            disabled={!canEdit}
                                                            className="h-9"
                                                        />
                                                        {nameError ? (
                                                            <p className="text-sm text-destructive">{nameError}</p>
                                                        ) : (
                                                            <p className="text-xs text-muted-foreground">Maximum {MAX_NAME_LENGTH} characters</p>
                                                        )}
                                                    </div>


                                                     <div className="space-y-2">
                                                         <Label htmlFor="privacyUrl" className="flex items-center gap-2">
                                                             <Link className="h-4 w-4 text-primary" />
                                                             Privacy Policy URL
                                                         </Label>
                                                         <Input
                                                             id="privacyUrl"
                                                             value={config.uiConfig?.privacyPolicyUrl || ''}
                                                             onChange={(e) => handleUIConfigUpdate({ privacyPolicyUrl: e.target.value })}
                                                             placeholder="https://yoursite.com/privacy"
                                                             disabled={!canEdit}
                                                             className="h-9"
                                                         />
                                                         <p className="text-xs text-muted-foreground">
                                                             Link to your privacy policy (optional)
                                                         </p>
                                                     </div>

                                                     <div className="space-y-2">
                                                         <Label htmlFor="allowedOrigins" className="flex items-center gap-2">
                                                             <Globe className="h-4 w-4 text-primary" />
                                                             Allowed Origins
                                                         </Label>
                                                         <p className="text-xs text-muted-foreground">
                                                             Restrict widget usage to specific domains. Use '*' to allow all domains.
                                                         </p>
                                                         
                                                         <div className="flex space-x-2">
                                                             <Input
                                                                 id="allowedOrigins"
                                                                 value={newOrigin}
                                                                 onChange={(e) => setNewOrigin(e.target.value)}
                                                                 placeholder="https://example.com or *"
                                                                 disabled={!canEdit}
                                                                 className="h-9"
                                                                 onKeyPress={(e) => {
                                                                     if (e.key === 'Enter') {
                                                                         e.preventDefault();
                                                                         addOrigin();
                                                                     }
                                                                 }}
                                                             />
                                                             <Button
                                                                 type="button"
                                                                 variant="outline"
                                                                 size="icon"
                                                                 onClick={addOrigin}
                                                                 disabled={!canEdit || !newOrigin.trim()}
                                                             >
                                                                 <Plus className="h-4 w-4" />
                                                             </Button>
                                                         </div>

                                                         {config.allowedOrigins && config.allowedOrigins.length > 0 && (
                                                             <div className="space-y-2 mt-3">
                                                                 <p className="text-sm font-medium">Allowed Origins:</p>
                                                                 <div className="space-y-1">
                                                                     {config.allowedOrigins.map((origin, index) => (
                                                                         <div
                                                                             key={index}
                                                                             className="flex items-center justify-between bg-muted/50 rounded-md px-3 py-2"
                                                                         >
                                                                             <span className="text-sm font-mono">{origin}</span>
                                                                             <Button
                                                                                 type="button"
                                                                                 variant="ghost"
                                                                                 size="icon"
                                                                                 className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                                                                 onClick={() => removeOrigin(origin)}
                                                                                 disabled={!canEdit}
                                                                             >
                                                                                 <X className="h-3 w-3" />
                                                                             </Button>
                                                                         </div>
                                                                     ))}
                                                                 </div>
                                                             </div>
                                                         )}
                                                     </div>
                                                 </>
                                             )}
                                         </CardContent>
                                     </Card>
                                     </motion.div>
                                 </TabsContent>

                                {/* Chat Tab */}
                                <TabsContent value="chat" className="m-0">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <Card className="shadow-sm">
                                        <CardHeader className="border-b bg-muted/20">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                                    <MessageSquare className="h-4 w-4 text-primary" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <CardTitle>Chat Configuration</CardTitle>
                                                    <CardDescription>Configure chat interface, messaging, and webhook integration</CardDescription>
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-6">
                                            {/* Enable Chat Toggle */}
                                            <SwitchWithLabel
                                                label="Enable Chat"
                                                icon={MessageSquare}
                                                tooltip="Enable chat messaging functionality for your widget"
                                                checked={config.chatEnabled ?? true}
                                                onCheckedChange={(checked) => handleConfigUpdate({ chatEnabled: checked })}
                                                disabled={!canEdit}
                                            />

                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Webhook className="h-4 w-4 text-primary" />
                                                    <Label htmlFor="webhookUrl">
                                                        Webhook URL{config.chatEnabled && <span className="text-destructive ml-1">*</span>}
                                                    </Label>
                                                    <TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-md">
                                                                <p>The endpoint URL must be in the format: https://workflow.voicebot.studio/api/v1/webhooks/[ID]/sync</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                                <Input
                                                    id="webhookUrl"
                                                    value={webhookUrl}
                                                    onChange={handleWebhookUrlChange}
                                                    onBlur={handleWebhookUrlBlur}
                                                    placeholder="https://workflow.voicebot.studio/api/v1/webhooks/[ID]/sync"
                                                    disabled={!canEdit}
                                                    className={webhookError ? "h-9 border-destructive" : "h-9"}
                                                />
                                                {webhookError ? (
                                                    <p className="text-sm text-destructive">{webhookError}</p>
                                                ) : (
                                                    <p className="text-xs text-muted-foreground">
                                                        URL to send chat messages to your webhook
                                                    </p>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div key="mainCardTitle" className="space-y-2">
                                                    <Label htmlFor="mainCardTitle" className="flex items-center gap-2">
                                                        <Type className="h-4 w-4 text-primary" />
                                                        Main Card Title
                                                    </Label>
                                                    <Input
                                                        id="mainCardTitle"
                                                        value={config.uiConfig?.mainCardTitle || ''}
                                                        onChange={(e) => handleUIConfigUpdate({ mainCardTitle: e.target.value })}
                                                        placeholder="Send us message"
                                                        disabled={!canEdit}
                                                        className="h-9"
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Main title displayed on the chat card
                                                    </p>
                                                </div>
                                                <div key="mainCardDescription" className="space-y-2">
                                                    <Label htmlFor="mainCardDescription" className="flex items-center gap-2">
                                                        <Type className="h-4 w-4 text-primary" />
                                                        Main Card Description
                                                    </Label>
                                                    <Input
                                                        id="mainCardDescription"
                                                        value={config.uiConfig?.mainCardDescription || ''}
                                                        onChange={(e) => handleUIConfigUpdate({ mainCardDescription: e.target.value })}
                                                        placeholder="Usual reply time is a few seconds"
                                                        disabled={!canEdit}
                                                        className="h-9"
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Description text below the main title
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div key="chatHeaderTitle" className="space-y-2">
                                                    <Label htmlFor="chatHeaderTitle" className="flex items-center gap-2">
                                                        <Type className="h-4 w-4 text-primary" />
                                                        Chat Header Title
                                                    </Label>
                                                    <Input
                                                        id="chatHeaderTitle"
                                                        value={config.uiConfig?.chatHeaderTitle || ''}
                                                        onChange={(e) => handleUIConfigUpdate({ chatHeaderTitle: e.target.value })}
                                                        placeholder="Sales Assistant"
                                                        disabled={!canEdit}
                                                        className="h-9"
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Title shown in the chat window header
                                                    </p>
                                                </div>
                                                <div key="chatHeaderSubtitle" className="space-y-2">
                                                    <Label htmlFor="chatHeaderSubtitle" className="flex items-center gap-2">
                                                        <Type className="h-4 w-4 text-primary" />
                                                        Chat Header Subtitle
                                                    </Label>
                                                    <Input
                                                        id="chatHeaderSubtitle"
                                                        value={config.uiConfig?.chatHeaderSubtitle || ''}
                                                        onChange={(e) => handleUIConfigUpdate({ chatHeaderSubtitle: e.target.value })}
                                                        placeholder="Always here to help you"
                                                        disabled={!canEdit}
                                                        className="h-9"
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Subtitle below the chat header title
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <MessageSquare className="h-4 w-4 text-primary" />
                                                    <Label htmlFor="welcomeMessage">Welcome Message</Label>
                                                </div>
                                                <Textarea
                                                    id="welcomeMessage"
                                                    value={config.uiConfig?.welcomeMessage || ''}
                                                    onChange={(e) => handleUIConfigUpdate({ welcomeMessage: e.target.value })}
                                                    placeholder="👋 Hello! How can I be of assistance today?"
                                                    disabled={!canEdit}
                                                    rows={3}
                                                />
                                                <p className="text-xs text-muted-foreground">
                                                    First message displayed when chat opens
                                                </p>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Type className="h-4 w-4 text-primary" />
                                                    <Label htmlFor="placeholder">Message Placeholder</Label>
                                                </div>
                                                <Input
                                                    id="placeholder"
                                                    value={config.uiConfig?.placeholder || ''}
                                                    onChange={(e) => handleUIConfigUpdate({ placeholder: e.target.value })}
                                                    placeholder="Type your message..."
                                                    disabled={!canEdit}
                                                    className="h-9"
                                                />
                                                <p className="text-xs text-muted-foreground">
                                                    Placeholder text in the chat input field
                                                </p>
                                            </div>

                                        </CardContent>
                                    </Card>
                                    </motion.div>
                                </TabsContent>

                                {/* Voice Tab */}
                                <TabsContent value="voice" className="m-0">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <Card className="shadow-sm">
                                        <CardHeader className="border-b bg-muted/20">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                                    <Phone className="h-4 w-4 text-primary" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <CardTitle>Voice Configuration</CardTitle>
                                                    <CardDescription>Configure AI assistant and voice calling capabilities</CardDescription>
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-6">
                                            {/* Enable Voice Calls Toggle */}
                                            <SwitchWithLabel
                                                label="Enable Voice Calls"
                                                icon={Phone}
                                                tooltip="Enable AI-powered voice calling for your widget"
                                                checked={config.voiceConfig?.enabled || false}
                                                onCheckedChange={(checked) => handleVoiceConfigUpdate({ enabled: checked })}
                                                disabled={!canEdit}
                                            />

                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Bot className="h-4 w-4 text-primary" />
                                                    <Label htmlFor="voiceAssistant">
                                                        Voice Assistant{config.voiceConfig?.enabled && <span className="text-destructive ml-1">*</span>}
                                                    </Label>
                                                    <TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-sm">
                                                                <p>Select the AI assistant that will handle voice calls and conversations</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                                {isLoadingData ? (
                                                    <Skeleton className="h-9 w-full" />
                                                ) : (
                                                    <Combobox
                                                        options={assistants.map(assistant => ({
                                                            value: assistant.id,
                                                            label: assistant.name,
                                                            description: assistant.id
                                                        }))}
                                                        value={config.voiceConfig?.assistantId || ''}
                                                        onValueChange={(value) => handleVoiceConfigUpdate({ assistantId: value })}
                                                        placeholder="Select voice assistant..."
                                                        searchPlaceholder="Search assistants..."
                                                        emptyMessage="No assistants found."
                                                        disabled={!canEdit}
                                                    />
                                                )}
                                                <p className="text-xs text-muted-foreground">
                                                    AI assistant that will handle voice calls
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div key="voiceButtonTitle" className="space-y-2">
                                                    <Label htmlFor="voiceButtonTitle" className="flex items-center gap-2">
                                                        <Type className="h-4 w-4 text-primary" />
                                                        Voice Button Title
                                                    </Label>
                                                    <Input
                                                        id="voiceButtonTitle"
                                                        value={config.voiceConfig?.title || ''}
                                                        onChange={(e) => handleVoiceConfigUpdate({ title: e.target.value })}
                                                        placeholder="Start Voice Call"
                                                        disabled={!canEdit}
                                                        className="h-9"
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Title displayed on the voice call button
                                                    </p>
                                                </div>
                                                <div key="voiceButtonDescription" className="space-y-2">
                                                    <Label htmlFor="voiceButtonDescription" className="flex items-center gap-2">
                                                        <FileText className="h-4 w-4 text-primary" />
                                                        Voice Button Description
                                                    </Label>
                                                    <Input
                                                        id="voiceButtonDescription"
                                                        value={config.voiceConfig?.description || ''}
                                                        onChange={(e) => handleVoiceConfigUpdate({ description: e.target.value })}
                                                        placeholder="Talk directly with our AI assistant"
                                                        disabled={!canEdit}
                                                        className="h-9"
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Description text below the button title
                                                    </p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                    </motion.div>
                                </TabsContent>

                                {/* UI Tab */}
                                <TabsContent value="ui" className="m-0">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <Card className="shadow-sm">
                                        <CardHeader className="border-b bg-muted/20">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                                    <Palette className="h-4 w-4 text-primary" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <CardTitle>Appearance</CardTitle>
                                                    <CardDescription>Customize colors, position, and visual elements</CardDescription>
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-6">
                                            {/* Text Content Section */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div key="widgetTitle" className="space-y-2">
                                                    <Label htmlFor="widgetTitle" className="flex items-center gap-2">
                                                        <Type className="h-4 w-4 text-primary" />
                                                        Main Widget Title
                                                    </Label>
                                                    <Input
                                                        id="widgetTitle"
                                                        value={config.uiConfig?.title || ''}
                                                        onChange={(e) => handleUIConfigUpdate({ title: e.target.value })}
                                                        placeholder="Chat with us"
                                                        disabled={!canEdit}
                                                        className="h-9"
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Main heading displayed on the widget
                                                    </p>
                                                </div>
                                                <div key="widgetSubtitle" className="space-y-2">
                                                    <Label htmlFor="widgetSubtitle" className="flex items-center gap-2">
                                                        <FileText className="h-4 w-4 text-primary" />
                                                        Main Widget Subtitle
                                                    </Label>
                                                    <Input
                                                        id="widgetSubtitle"
                                                        value={config.uiConfig?.subtitle || ''}
                                                        onChange={(e) => handleUIConfigUpdate({ subtitle: e.target.value })}
                                                        placeholder="We're here to help"
                                                        disabled={!canEdit}
                                                        className="h-9"
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Subtitle text below the main title
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div key="toggleText" className="space-y-2">
                                                    <Label htmlFor="toggleText" className="flex items-center gap-2">
                                                        <Type className="h-4 w-4 text-primary" />
                                                        Toggle Text
                                                    </Label>
                                                    <Input
                                                        id="toggleText"
                                                        value={config.uiConfig?.toggleText || ''}
                                                        onChange={(e) => handleUIConfigUpdate({ toggleText: e.target.value })}
                                                        placeholder="Talk to us"
                                                        disabled={!canEdit}
                                                        className="h-9"
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Text displayed on the widget toggle button
                                                    </p>
                                                </div>
                                                <div key="fontFamily" className="space-y-2">
                                                    <Label htmlFor="fontFamily" className="flex items-center gap-2">
                                                        <Type className="h-4 w-4 text-primary" />
                                                        Font Family
                                                    </Label>
                                                    <Combobox
                                                        options={[
                                                            ...fontOptions
                                                                .filter(font => font.popular)
                                                                .map(font => ({
                                                                    value: font.value,
                                                                    label: font.label,
                                                                    description: 'Popular'
                                                                })),
                                                            ...fontOptions
                                                                .filter(font => !font.popular)
                                                                .map(font => ({
                                                                    value: font.value,
                                                                    label: font.label
                                                                }))
                                                        ]}
                                                        value={config.uiConfig?.fontFamily || 'inter'}
                                                        onValueChange={(value) => handleUIConfigUpdate({ fontFamily: value })}
                                                        placeholder="Select font..."
                                                        searchPlaceholder="Search fonts..."
                                                        emptyMessage="No fonts found."
                                                        disabled={!canEdit}
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Font family for widget text
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Colors Section */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div key="brandColor" className="space-y-2">
                                                    <Label htmlFor="brandColor" className="flex items-center gap-2">
                                                        <Paintbrush className="h-4 w-4 text-primary" />
                                                        Brand Color
                                                    </Label>
                                                    <div className="relative flex items-center gap-2">
                                                        <div className="relative">
                                                            <Input
                                                                type="color"
                                                                value={config.uiConfig?.brandColor || '#036ffc'}
                                                                onChange={(e) => handleUIConfigUpdate({ brandColor: e.target.value })}
                                                                className="absolute inset-0 w-12 h-9 p-0 border-0 rounded-md cursor-pointer opacity-0"
                                                                disabled={!canEdit}
                                                            />
                                                            <div 
                                                                className="w-12 h-9 rounded-md border-2 border-input cursor-pointer hover:border-primary transition-colors"
                                                                style={{ backgroundColor: config.uiConfig?.brandColor || '#036ffc' }}
                                                            />
                                                        </div>
                                                        <Input
                                                            id="brandColor"
                                                            value={config.uiConfig?.brandColor || '#036ffc'}
                                                            onChange={(e) => handleUIConfigUpdate({ brandColor: e.target.value })}
                                                            placeholder="#036ffc"
                                                            className="flex-1 h-9 font-mono text-sm uppercase"
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">
                                                        Primary brand color for the widget
                                                    </p>
                                                </div>

                                                <div key="mainCardColor" className="space-y-2">
                                                    <Label htmlFor="mainCardColor" className="flex items-center gap-2">
                                                        <Palette className="h-4 w-4 text-primary" />
                                                        Main Card Color
                                                    </Label>
                                                    <div className="relative flex items-center gap-2">
                                                        <div className="relative">
                                                            <Input
                                                                type="color"
                                                                value={config.uiConfig?.mainCardColor || '#036ffc'}
                                                                onChange={(e) => handleUIConfigUpdate({ mainCardColor: e.target.value })}
                                                                className="absolute inset-0 w-12 h-9 p-0 border-0 rounded-md cursor-pointer opacity-0"
                                                                disabled={!canEdit}
                                                            />
                                                            <div 
                                                                className="w-12 h-9 rounded-md border-2 border-input cursor-pointer hover:border-primary transition-colors"
                                                                style={{ backgroundColor: config.uiConfig?.mainCardColor || '#036ffc' }}
                                                            />
                                                        </div>
                                                        <Input
                                                            id="mainCardColor"
                                                            value={config.uiConfig?.mainCardColor || '#036ffc'}
                                                            onChange={(e) => handleUIConfigUpdate({ mainCardColor: e.target.value })}
                                                            placeholder="#036ffc"
                                                            className="flex-1 h-9 font-mono text-sm uppercase"
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">
                                                        Background color for the main card
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Position Section */}
                                            <div className="space-y-2">
                                                <Label htmlFor="position" className="flex items-center gap-2">
                                                    <MapPin className="h-4 w-4 text-primary" />
                                                    Widget Position
                                                </Label>
                                                <Combobox
                                                    options={[
                                                        { value: 'bottom-right', label: 'Bottom Right', description: 'Default position' },
                                                        { value: 'bottom-left', label: 'Bottom Left' },
                                                        { value: 'top-right', label: 'Top Right' },
                                                        { value: 'top-left', label: 'Top Left' }
                                                    ]}
                                                    value={config.uiConfig?.position || 'bottom-right'}
                                                    onValueChange={(value) => value && handleUIConfigUpdate({ position: value as 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' })}
                                                    placeholder="Select position..."
                                                    searchPlaceholder="Search positions..."
                                                    emptyMessage="No positions found."
                                                    disabled={!canEdit}
                                                    className="max-w-xs"
                                                />
                                                <p className="text-xs text-muted-foreground">
                                                    Screen position where the widget will appear
                                                </p>
                                            </div>

                                            {/* Icons Section */}
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                <div className="space-y-2">
                                                    <Label className="flex items-center gap-2">
                                                        <LayoutGrid className="h-4 w-4 text-primary" />
                                                        Toggle Icon
                                                    </Label>
                                                    <IconPicker
                                                        value={config.uiConfig?.toggleIcon}
                                                        onChange={(icon) => handleUIConfigUpdate({ toggleIcon: icon })}
                                                        placeholder="Select toggle icon"
                                                        disabled={!canEdit}
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Icon for toggle button
                                                    </p>
                                                </div>

                                                <div className="space-y-2">
                                                    <Label className="flex items-center gap-2">
                                                        <LayoutGrid className="h-4 w-4 text-primary" />
                                                        Main Card Icon
                                                    </Label>
                                                    <IconPicker
                                                        value={config.uiConfig?.mainCardIcon}
                                                        onChange={(icon) => handleUIConfigUpdate({ mainCardIcon: icon })}
                                                        placeholder="Select main card icon"
                                                        disabled={!canEdit}
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Icon for main card header
                                                    </p>
                                                </div>

                                                <div className="space-y-2">
                                                    <Label className="flex items-center gap-2">
                                                        <Phone className="h-4 w-4 text-primary" />
                                                        Voice Button Icon
                                                    </Label>
                                                    <IconPicker
                                                        value={config.voiceConfig?.icon}
                                                        onChange={(icon) => handleVoiceConfigUpdate({ icon })}
                                                        placeholder="Select voice icon"
                                                        disabled={!canEdit}
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Icon for voice call button
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Rainbow Ring Effect */}
                                            <SwitchWithLabel
                                                label="Enable Rainbow Ring Effect"
                                                icon={Wand2}
                                                tooltip="Add an animated rainbow ring around the widget toggle button"
                                                checked={config.uiConfig?.enableRainbowRing || false}
                                                onCheckedChange={(checked) => handleUIConfigUpdate({ enableRainbowRing: checked })}
                                                disabled={!canEdit}
                                            />

                                            {config.uiConfig?.enableRainbowRing && (
                                                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                                                    <div className="flex items-center justify-between">
                                                        <Label className="text-sm font-medium flex items-center gap-2">
                                                            <Sparkles className="h-4 w-4" />
                                                            Rainbow Ring Colors
                                                        </Label>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => {
                                                                const currentColors = config.uiConfig?.rainbowRingColors || ['#ff0080'];
                                                                if (currentColors.length < 8) {
                                                                    const newColors = [...currentColors, '#ff0080'];
                                                                    handleUIConfigUpdate({ rainbowRingColors: newColors });
                                                                }
                                                            }}
                                                            disabled={!canEdit || (config.uiConfig?.rainbowRingColors || ['#ff0080']).length >= 8}
                                                        >
                                                            <Plus className="h-4 w-4 mr-2" />
                                                            Add Color
                                                        </Button>
                                                    </div>
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                        {(config.uiConfig?.rainbowRingColors || ['#ff0080']).map((color, index) => (
                                                            <div key={index} className="space-y-2">
                                                                <div className="flex items-center justify-between">
                                                                    <Label className="text-xs">Color {index + 1}</Label>
                                                                    {(config.uiConfig?.rainbowRingColors || ['#ff0080']).length > 1 && (
                                                                        <Button
                                                                            type="button"
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            onClick={() => {
                                                                                const currentColors = config.uiConfig?.rainbowRingColors || ['#ff0080'];
                                                                                const newColors = currentColors.filter((_, i) => i !== index);
                                                                                handleUIConfigUpdate({ rainbowRingColors: newColors });
                                                                            }}
                                                                            disabled={!canEdit}
                                                                            className="h-6 w-6 hover:bg-destructive hover:text-destructive-foreground"
                                                                        >
                                                                            <X className="h-3 w-3" />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                                <Input
                                                                    type="color"
                                                                    value={color}
                                                                    onChange={(e) => {
                                                                        const currentColors = config.uiConfig?.rainbowRingColors || ['#ff0080'];
                                                                        const newColors = [...currentColors];
                                                                        newColors[index] = e.target.value;
                                                                        handleUIConfigUpdate({ rainbowRingColors: newColors });
                                                                    }}
                                                                    className="w-full h-10 p-1 cursor-pointer"
                                                                    disabled={!canEdit}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">
                                                        Add 1-8 colors for the rainbow ring animation. At least 1 color is required.
                                                    </p>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                    </motion.div>
                                </TabsContent>

                                {/* Cards Tab */}
                                <TabsContent value="cards" className="m-0">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <div className="space-y-6">
                                        {/* WhatsApp Card */}
                                        <Card className="shadow-sm">
                                        <CardHeader className="border-b bg-muted/20">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                                    <MessageSquare className="h-4 w-4 text-primary" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <CardTitle>WhatsApp Card</CardTitle>
                                                    <CardDescription>Configure WhatsApp contact option</CardDescription>
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-6">
                                            <SwitchWithLabel
                                                label="Show WhatsApp Card"
                                                icon={ToggleLeft}
                                                tooltip="Display WhatsApp contact option in the widget"
                                                checked={config.uiConfig?.showWhatsApp || false}
                                                onCheckedChange={(checked) => handleUIConfigUpdate({ showWhatsApp: checked })}
                                                disabled={!canEdit}
                                            />

                                            {config.uiConfig?.showWhatsApp && (
                                                <div className="space-y-6">
                                                    <div className="space-y-2">
                                                        <Label htmlFor="whatsappLabel" className="flex items-center gap-2">
                                                            <Type className="h-4 w-4 text-primary" />
                                                            WhatsApp Label
                                                        </Label>
                                                        <Input
                                                            id="whatsappLabel"
                                                            value={config.uiConfig?.whatsAppLabel || ''}
                                                            onChange={(e) => handleUIConfigUpdate({ whatsAppLabel: e.target.value })}
                                                            placeholder="Contact us on WhatsApp"
                                                            disabled={!canEdit}
                                                            className="h-9"
                                                        />
                                                        <p className="text-xs text-muted-foreground">
                                                            Display text for the WhatsApp card
                                                        </p>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label htmlFor="whatsappUrl" className="flex items-center gap-2">
                                                            <Link className="h-4 w-4 text-primary" />
                                                            WhatsApp URL
                                                        </Label>
                                                        <Input
                                                            id="whatsappUrl"
                                                            value={config.uiConfig?.whatsAppUrl || ''}
                                                            onChange={(e) => handleUIConfigUpdate({ whatsAppUrl: e.target.value })}
                                                            placeholder="https://wa.me/15551234567"
                                                            disabled={!canEdit}
                                                            className="h-9"
                                                        />
                                                        <p className="text-xs text-muted-foreground">
                                                            WhatsApp link in wa.me format (e.g., https://wa.me/15551234567)
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>

                                    {/* Call Card */}
                                    <Card className="shadow-sm">
                                        <CardHeader className="border-b bg-muted/20">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                                    <Phone className="h-4 w-4 text-primary" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <CardTitle>Call Card</CardTitle>
                                                    <CardDescription>Configure phone call option</CardDescription>
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-6">
                                            <SwitchWithLabel
                                                label="Show Call Card"
                                                icon={ToggleLeft}
                                                tooltip="Display phone call option in the widget"
                                                checked={config.uiConfig?.showCall || false}
                                                onCheckedChange={(checked) => handleUIConfigUpdate({ showCall: checked })}
                                                disabled={!canEdit}
                                            />

                                            {config.uiConfig?.showCall && (
                                                <div className="space-y-6">
                                                    <div className="space-y-2">
                                                        <Label htmlFor="callLabel" className="flex items-center gap-2">
                                                            <Type className="h-4 w-4 text-primary" />
                                                            Call Label
                                                        </Label>
                                                        <Input
                                                            id="callLabel"
                                                            value={config.uiConfig?.callLabel || ''}
                                                            onChange={(e) => handleUIConfigUpdate({ callLabel: e.target.value })}
                                                            placeholder="Contact us by phone"
                                                            disabled={!canEdit}
                                                            className="h-9"
                                                        />
                                                        <p className="text-xs text-muted-foreground">
                                                            Display text for the call card
                                                        </p>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        <div className="space-y-2">
                                                            <Label htmlFor="callUrl" className="flex items-center gap-2">
                                                                <Link className="h-4 w-4 text-primary" />
                                                                Call URL
                                                            </Label>
                                                            <Input
                                                                id="callUrl"
                                                                value={config.uiConfig?.callUrl || ''}
                                                                onChange={(e) => handleUIConfigUpdate({ callUrl: e.target.value })}
                                                                placeholder="tel:+15551234567"
                                                                disabled={!canEdit}
                                                                className="h-9"
                                                            />
                                                            <p className="text-xs text-muted-foreground">
                                                                Phone call link in tel: format
                                                            </p>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label htmlFor="phoneNumber" className="flex items-center gap-2">
                                                                <Phone className="h-4 w-4 text-primary" />
                                                                Phone Number
                                                            </Label>
                                                            <Input
                                                                id="phoneNumber"
                                                                value={config.uiConfig?.phoneNumber || ''}
                                                                onChange={(e) => handleUIConfigUpdate({ phoneNumber: e.target.value })}
                                                                placeholder="+15551234567"
                                                                disabled={!canEdit}
                                                                className="h-9"
                                                            />
                                                            <p className="text-xs text-muted-foreground">
                                                                Display phone number for users
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>

                                    {/* Custom Cards */}
                                    <Card className="shadow-sm">
                                        <CardHeader className="border-b bg-muted/20">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                                    <Sparkles className="h-4 w-4 text-primary" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <CardTitle>Custom Cards</CardTitle>
                                                    <CardDescription>Add custom action cards to your widget</CardDescription>
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-6">
                                            {config.uiConfig?.customCards && config.uiConfig.customCards.length > 0 ? (
                                                <div className="space-y-4">
                                                    {config.uiConfig.customCards.map((card, index) => (
                                                        <Card key={card.id} className="shadow-sm">
                                                            {/* Card Header with Toggle and Remove */}
                                                            <CardHeader className="border-b bg-muted/20">
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex items-center gap-3">
                                                                        <Switch
                                                                            checked={card.enabled}
                                                                            onCheckedChange={(checked) => {
                                                                                const updatedCards = [...(config.uiConfig?.customCards || [])];
                                                                                updatedCards[index] = { ...card, enabled: checked };
                                                                                handleUIConfigUpdate({ customCards: updatedCards });
                                                                            }}
                                                                            disabled={!canEdit}
                                                                        />
                                                                        <CardTitle className="text-sm">{card.title || 'Untitled Card'}</CardTitle>
                                                                    </div>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        onClick={() => {
                                                                            const updatedCards = config.uiConfig?.customCards?.filter((_, i) => i !== index) || [];
                                                                            handleUIConfigUpdate({ customCards: updatedCards });
                                                                        }}
                                                                        disabled={!canEdit}
                                                                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            </CardHeader>
                                                            
                                                            <CardContent className="space-y-6">
                                                                {/* Title and Description */}
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                                    <div key="title" className="space-y-2">
                                                        <Label className="flex items-center gap-2">
                                                            <Type className="h-4 w-4 text-primary" />
                                                            Card Title
                                                        </Label>
                                                                        <Input
                                                                            value={card.title}
                                                                            onChange={(e) => {
                                                                                const updatedCards = [...(config.uiConfig?.customCards || [])];
                                                                                updatedCards[index] = { ...card, title: e.target.value };
                                                                                handleUIConfigUpdate({ customCards: updatedCards });
                                                                            }}
                                                                            placeholder="Card title"
                                                                            disabled={!canEdit}
                                                                            className="h-9"
                                                                        />
                                                                        <p className="text-xs text-muted-foreground">
                                                                            Title displayed on the custom card
                                                                        </p>
                                                                    </div>
                                                                    <div key="description" className="space-y-2">
                                                        <Label className="flex items-center gap-2">
                                                            <FileText className="h-4 w-4 text-primary" />
                                                            Card Description
                                                        </Label>
                                                                        <Input
                                                                            value={card.description}
                                                                            onChange={(e) => {
                                                                                const updatedCards = [...(config.uiConfig?.customCards || [])];
                                                                                updatedCards[index] = { ...card, description: e.target.value };
                                                                                handleUIConfigUpdate({ customCards: updatedCards });
                                                                            }}
                                                                            placeholder="Card description"
                                                                            disabled={!canEdit}
                                                                            className="h-9"
                                                                        />
                                                                        <p className="text-xs text-muted-foreground">
                                                                            Description text below the title
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Icon, URL, and Color */}
                                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                                    <div key="icon" className="space-y-2">
                                                        <Label className="flex items-center gap-2">
                                                            <LayoutGrid className="h-4 w-4 text-primary" />
                                                            Icon
                                                        </Label>
                                                                        <IconPicker
                                                                            value={card.icon}
                                                                            onChange={(icon) => {
                                                                                const updatedCards = [...(config.uiConfig?.customCards || [])];
                                                                                updatedCards[index] = { ...card, icon };
                                                                                handleUIConfigUpdate({ customCards: updatedCards });
                                                                            }}
                                                                            placeholder="Select icon"
                                                                            disabled={!canEdit}
                                                                        />
                                                                        <p className="text-xs text-muted-foreground">
                                                                            Icon displayed on the card
                                                                        </p>
                                                                    </div>
                                                                    <div key="url" className="space-y-2">
                                                        <Label className="flex items-center gap-2">
                                                            <Link className="h-4 w-4 text-primary" />
                                                            URL
                                                        </Label>
                                                                        <Input
                                                                            value={card.url}
                                                                            onChange={(e) => {
                                                                                const updatedCards = [...(config.uiConfig?.customCards || [])];
                                                                                updatedCards[index] = { ...card, url: e.target.value };
                                                                                handleUIConfigUpdate({ customCards: updatedCards });
                                                                            }}
                                                                            placeholder="https://example.com"
                                                                            disabled={!canEdit}
                                                                            className="h-9"
                                                                        />
                                                                        <p className="text-xs text-muted-foreground">
                                                                            Link to open when card is clicked
                                                                        </p>
                                                                    </div>
                                                                    <div key="color" className="space-y-2">
                                                        <Label className="flex items-center gap-2">
                                                            <Paintbrush className="h-4 w-4 text-primary" />
                                                            Color
                                                        </Label>
                                                                        <div className="relative">
                                                                            <Input
                                                                                type="color"
                                                                                value={card.color}
                                                                                onChange={(e) => {
                                                                                    const updatedCards = [...(config.uiConfig?.customCards || [])];
                                                                                    updatedCards[index] = { ...card, color: e.target.value };
                                                                                    handleUIConfigUpdate({ customCards: updatedCards });
                                                                                }}
                                                                                disabled={!canEdit}
                                                                                className="absolute inset-0 w-full h-9 p-0 border-0 rounded-md cursor-pointer opacity-0"
                                                                            />
                                                                            <div 
                                                                                className="w-full h-9 rounded-md border-2 border-input cursor-pointer hover:border-primary transition-colors"
                                                                                style={{ backgroundColor: card.color }}
                                                                            />
                                                                        </div>
                                                                        <p className="text-xs text-muted-foreground">
                                                                            Card accent color
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-12 text-muted-foreground">
                                                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                                        <Sparkles className="h-8 w-8" />
                                                    </div>
                                                    <h3 className="text-sm font-medium mb-1">No Custom Cards</h3>
                                                    <p className="text-xs">Add custom action cards to extend your widget functionality</p>
                                                </div>
                                            )}

                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    const newCard = {
                                                        id: `custom-${Date.now()}`,
                                                        title: 'New Card',
                                                        description: 'Card description',
                                                        icon: 'help-circle',
                                                        url: 'https://example.com',
                                                        color: '#3b82f6',
                                                        enabled: true
                                                    };
                                                    const updatedCards = [...(config.uiConfig?.customCards || []), newCard];
                                                    handleUIConfigUpdate({ customCards: updatedCards });
                                                }}
                                                disabled={!canEdit}
                                                className="w-full"
                                            >
                                                <Plus className="h-4 w-4 mr-2" />
                                                Add Custom Card
                                            </Button>
                                        </CardContent>
                                    </Card>
                                    </div>
                                    </motion.div>
                                </TabsContent>

                                {/* Integration Tab */}
                                <TabsContent value="integration" className="m-0">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                    {widgetData ? (
                                        <WidgetIntegrationGuide widgetData={widgetData} />
                                    ) : (
                                        <Card className="shadow-sm">
                                            <CardHeader className="border-b bg-muted/20">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                                        <Code className="h-4 w-4 text-primary" />
                                                    </div>
                                                    <div className="flex-1 space-y-1">
                                                        <CardTitle>Integration Guide</CardTitle>
                                                        <CardDescription>Integration instructions will be available after saving your widget</CardDescription>
                                                    </div>
                                                </div>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="flex items-center justify-center py-12">
                                                    <div className="text-center space-y-3">
                                                        <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                                                            <Code className="h-8 w-8 text-muted-foreground" />
                                                        </div>
                                                        <div>
                                                            <h3 className="text-lg font-medium">Save Widget First</h3>
                                                            <p className="text-sm text-muted-foreground mt-1">
                                                                Complete your widget configuration and save it to access integration code and documentation.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    )}
                                    </motion.div>
                                </TabsContent>
                            </Tabs>
                        </div>

                        {/* Preview Section */}
                        <div key="preview-section" className="space-y-6">
                            <Card className="shadow-sm">
                                <CardHeader className="border-b bg-muted/20">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                            <Eye className="h-4 w-4 text-primary" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <CardTitle>Live Preview</CardTitle>
                                                <div className={`px-2.5 py-1 rounded-full text-xs font-medium border flex items-center gap-1.5 ${
                                                    hasUnsavedChanges 
                                                        ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-orange-200 dark:border-orange-800' 
                                                        : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800'
                                                }`}>
                                                    {hasUnsavedChanges ? (
                                                        <>
                                                            <AlertTriangle className="h-3 w-3" />
                                                            Unsaved
                                                        </>
                                                    ) : (
                                                        <>
                                                            <CheckCircle className="h-3 w-3" />
                                                            Saved
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <CardDescription>
                                                {hasUnsavedChanges 
                                                    ? "Save changes to update the preview" 
                                                    : "Preview shows widget appearance. API features require deployment to allowed origins."
                                                }
                                            </CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="h-[720px] p-4">
                                    <WidgetPreview 
                                        widgetData={widgetData || undefined} 
                                        updatedAt={widgetData?.updated_at}
                                        widgetId={widgetId}
                                        isCreateMode={!isEditing}
                                    />
                                </CardContent>
                            </Card>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}
